import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, Upload, Save, Trash2, Plus, Check, X,
  RefreshCw, Building2, ChevronLeft, ChevronRight, Wand2,
  FileCheck, AlertCircle, Loader2, Search, Zap, Link2, MapPin
} from 'lucide-react';
import { developerTemplateAPI } from '../../lib/api';
import api from '../../lib/api';

const AdminDeveloperTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [savingFields, setSavingFields] = useState(false);
  const [bulkDetecting, setBulkDetecting] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    developer_name: '',
    property_id: '',
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadTemplates();
    loadProperties();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await developerTemplateAPI.getTemplates();
      setTemplates(res.data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const res = await api.get('/properties');
      setProperties(res.data || []);
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    setCurrentPage(0);
    setPreviewImage(null);
    loadTemplateDetails(template.id);
    loadPagePreview(template.id, 0);
  };

  const loadTemplateDetails = async (templateId) => {
    try {
      const res = await developerTemplateAPI.getTemplate(templateId);
      setSelectedTemplate(res.data);
    } catch (error) {
      console.error('Error loading details:', error);
    }
  };

  const loadPagePreview = async (templateId, page) => {
    setPreviewLoading(true);
    try {
      const res = await developerTemplateAPI.getPreview(templateId || selectedTemplate?.id, page);
      setPreviewImage(res.data.image);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreviewImage(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    setUploadForm(prev => ({
      ...prev,
      name: file.name.replace('.pdf', '').replace(/[-_]/g, ' ')
    }));
    setShowUploadDialog(true);
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) { toast.error('Please select a file'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (uploadForm.name) formData.append('name', uploadForm.name);
      if (uploadForm.developer_name) formData.append('developer_name', uploadForm.developer_name);
      if (uploadForm.property_id) formData.append('property_id', uploadForm.property_id);

      const res = await developerTemplateAPI.uploadTemplate(formData);
      toast.success('Template uploaded! AI field detection started.');
      setShowUploadDialog(false);
      setUploadForm({ name: '', developer_name: '', property_id: '' });
      fileInputRef.current.value = '';
      await loadTemplates();
      handleSelectTemplate(res.data.template);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await developerTemplateAPI.deleteTemplate(templateId);
      toast.success('Template deleted');
      if (selectedTemplate?.id === templateId) setSelectedTemplate(null);
      await loadTemplates();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleActivate = async (templateId) => {
    try {
      await developerTemplateAPI.activateTemplate(templateId);
      toast.success('Template activated');
      await loadTemplates();
      if (selectedTemplate?.id === templateId) loadTemplateDetails(templateId);
    } catch (error) {
      toast.error('Failed to activate');
    }
  };

  const handleReDetect = async (templateId) => {
    try {
      await developerTemplateAPI.reDetect(templateId);
      toast.success('AI detection started. This may take 15-30 seconds...');
      // Poll for completion
      pollDetectionStatus(templateId);
    } catch (error) {
      toast.error('Failed to start detection');
    }
  };

  const handleDetectPositions = async (templateId) => {
    try {
      const res = await developerTemplateAPI.detectPositions(templateId);
      toast.success(res.data.message);
      loadTemplateDetails(templateId);
    } catch (error) {
      toast.error('Failed to detect positions');
    }
  };

  const pollDetectionStatus = (templateId) => {
    const interval = setInterval(async () => {
      try {
        const res = await developerTemplateAPI.getTemplate(templateId);
        if (res.data.ai_detection_status !== 'processing') {
          clearInterval(interval);
          if (res.data.ai_detection_status === 'completed') {
            toast.success(`AI detected ${res.data.field_count} fields!`);
          } else if (res.data.ai_detection_status === 'error') {
            toast.error(`AI detection failed: ${res.data.ai_error || 'Unknown error'}`);
          }
          setSelectedTemplate(res.data);
          await loadTemplates();
        }
      } catch (e) {
        clearInterval(interval);
      }
    }, 5000);
    // Stop polling after 3 minutes
    setTimeout(() => clearInterval(interval), 180000);
  };

  const handleBulkDetect = async () => {
    setBulkDetecting(true);
    try {
      const res = await developerTemplateAPI.bulkDetect();
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Bulk detection failed');
    } finally {
      setBulkDetecting(false);
    }
  };

  const handleSaveFields = async () => {
    if (!selectedTemplate) return;
    setSavingFields(true);
    try {
      await developerTemplateAPI.updateFields(selectedTemplate.id, selectedTemplate.fields);
      toast.success('Fields saved');
    } catch (error) {
      toast.error('Failed to save fields');
    } finally {
      setSavingFields(false);
    }
  };

  const handleLinkProperty = async (templateId, propertyId) => {
    try {
      await developerTemplateAPI.updateTemplate(templateId, { property_id: propertyId || null });
      toast.success(propertyId ? 'Linked to property' : 'Unlinked from property');
      await loadTemplates();
      if (selectedTemplate?.id === templateId) loadTemplateDetails(templateId);
    } catch (error) {
      toast.error('Failed to update link');
    }
  };

  const updateField = (fieldId, key, value) => {
    setSelectedTemplate(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, [key]: value } : f)
    }));
  };

  const toggleFieldEnabled = (fieldId) => {
    setSelectedTemplate(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, enabled: !f.enabled } : f)
    }));
  };

  // Filter templates
  const filteredTemplates = templates.filter(t => {
    const matchSearch = !searchQuery ||
      t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.developer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.project_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = filterStatus === 'all' || t.status === filterStatus ||
      (filterStatus === 'detected' && t.ai_detection_status === 'completed') ||
      (filterStatus === 'pending' && t.ai_detection_status === 'pending');
    return matchSearch && matchStatus;
  });

  // Group fields by section
  const groupedFields = selectedTemplate?.fields?.reduce((acc, field) => {
    const section = field.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {}) || {};

  // Stats
  const stats = {
    total: templates.length,
    active: templates.filter(t => t.status === 'active').length,
    detected: templates.filter(t => t.ai_detection_status === 'completed').length,
    pending: templates.filter(t => t.ai_detection_status === 'pending').length,
  };

  return (
    <div className="space-y-6" data-testid="admin-developer-templates">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-[#4E50FF]" />
            Developer PDF Templates
          </h1>
          <p className="text-slate-500 mt-1">
            {stats.total} templates | {stats.active} active | {stats.detected} AI-detected | {stats.pending} pending
          </p>
        </div>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
          <Button
            variant="outline"
            onClick={handleBulkDetect}
            disabled={bulkDetecting || stats.pending === 0}
            data-testid="bulk-detect-btn"
          >
            {bulkDetecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
            AI Detect All ({stats.pending})
          </Button>
          <Button onClick={() => fileInputRef.current?.click()} className="bg-[#4E50FF] hover:bg-[#4E50FF]/90" data-testid="upload-template-btn">
            <Upload className="h-4 w-4 mr-2" />
            Upload Template
          </Button>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Developer Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Template Name</Label>
              <Input value={uploadForm.name} onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Botanika Sales Form" className="mt-1" />
            </div>
            <div>
              <Label>Developer Name</Label>
              <Input value={uploadForm.developer_name} onChange={(e) => setUploadForm(prev => ({ ...prev, developer_name: e.target.value }))} placeholder="e.g., IOI Properties" className="mt-1" />
            </div>
            <div>
              <Label>Link to Property (Optional)</Label>
              <Select value={uploadForm.property_id || 'none'} onValueChange={(v) => setUploadForm(prev => ({ ...prev, property_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select a property..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {properties.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
              <Button onClick={handleUpload} disabled={uploading} className="bg-[#4E50FF] hover:bg-[#4E50FF]/90">
                {uploading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>) : (<><Wand2 className="h-4 w-4 mr-2" />Upload & Detect Fields</>)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Template List */}
        <Card className="border shadow-sm">
          <CardHeader className="pb-3">
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9 text-sm"
                  data-testid="search-templates"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ({stats.total})</SelectItem>
                  <SelectItem value="active">Active ({stats.active})</SelectItem>
                  <SelectItem value="detected">AI Detected ({stats.detected})</SelectItem>
                  <SelectItem value="pending">Pending ({stats.pending})</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="space-y-1 max-h-[600px] overflow-y-auto px-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No templates found</p>
              </div>
            ) : (
              filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  data-testid={`template-item-${template.id}`}
                  className={`p-2.5 rounded-lg cursor-pointer border transition-all ${
                    selectedTemplate?.id === template.id
                      ? 'border-[#4E50FF] bg-[#4E50FF]/5 shadow-sm'
                      : 'border-transparent hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileCheck className={`h-3.5 w-3.5 flex-shrink-0 ${
                      template.status === 'active' ? 'text-green-500' :
                      template.ai_detection_status === 'completed' ? 'text-[#4E50FF]' : 'text-slate-400'
                    }`} />
                    <span className="font-medium text-slate-900 text-xs truncate">{template.name}</span>
                  </div>
                  {template.developer_name && (
                    <p className="text-[10px] text-slate-500 ml-5 truncate">{template.developer_name}</p>
                  )}
                  {template.project_name && (
                    <p className="text-[10px] text-blue-500 ml-5 truncate">{template.project_name}</p>
                  )}
                  <div className="flex items-center gap-1 mt-1.5 ml-5 flex-wrap">
                    {template.ai_detection_status === 'completed' && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-600">
                        <Wand2 className="h-2.5 w-2.5 mr-0.5" />
                        {template.field_count} fields
                      </Badge>
                    )}
                    {template.ai_detection_status === 'processing' && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-600">
                        <Loader2 className="h-2.5 w-2.5 mr-0.5 animate-spin" />
                        Detecting...
                      </Badge>
                    )}
                    {template.ai_detection_status === 'pending' && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Pending</Badge>
                    )}
                    {template.ai_detection_status === 'error' && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-red-50 text-red-600">Error</Badge>
                    )}
                    {template.status === 'active' && (
                      <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700">Active</Badge>
                    )}
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{template.page_count}pg</Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Template Details */}
        <div className="lg:col-span-3">
          {selectedTemplate ? (
            <div className="space-y-4">
              {/* Header Card */}
              <Card className="border shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-slate-900">{selectedTemplate.name}</h2>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        {selectedTemplate.developer_name && <span>Developer: <strong className="text-slate-700">{selectedTemplate.developer_name}</strong></span>}
                        {selectedTemplate.project_name && <span>Project: <strong className="text-blue-600">{selectedTemplate.project_name}</strong></span>}
                      </div>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        <Badge variant="secondary">{selectedTemplate.page_count} pages</Badge>
                        {selectedTemplate.ai_detection_status === 'completed' && (
                          <Badge className="bg-purple-100 text-purple-700">
                            <Wand2 className="h-3 w-3 mr-1" />{selectedTemplate.field_count} AI fields
                          </Badge>
                        )}
                        {selectedTemplate.ai_detection_status === 'processing' && (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />Detecting...
                          </Badge>
                        )}
                        {selectedTemplate.ai_detection_status === 'error' && (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertCircle className="h-3 w-3 mr-1" />Detection Error
                          </Badge>
                        )}
                        {selectedTemplate.status === 'active' && (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        )}
                        {selectedTemplate.has_text && <Badge variant="outline" className="text-xs">Text-based</Badge>}
                        {!selectedTemplate.has_text && <Badge variant="outline" className="text-xs">Image-only</Badge>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button variant="outline" size="sm" onClick={() => handleReDetect(selectedTemplate.id)} data-testid="redetect-btn">
                        <Wand2 className="h-4 w-4 mr-1" />Re-detect
                      </Button>
                      {selectedTemplate.ai_detection_status === 'completed' && !selectedTemplate.positions_detected && (
                        <Button variant="outline" size="sm" onClick={() => handleDetectPositions(selectedTemplate.id)} className="text-purple-600" data-testid="detect-positions-btn">
                          <MapPin className="h-4 w-4 mr-1" />Map Positions
                        </Button>
                      )}
                      {selectedTemplate.positions_detected && (
                        <Badge className="bg-purple-50 text-purple-600 text-xs self-center"><MapPin className="h-3 w-3 mr-0.5 inline" />Mapped</Badge>
                      )}
                      {selectedTemplate.status !== 'active' && (
                        <Button size="sm" onClick={() => handleActivate(selectedTemplate.id)} className="bg-green-600 hover:bg-green-700" data-testid="activate-btn">
                          <Check className="h-4 w-4 mr-1" />Activate
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => handleDeleteTemplate(selectedTemplate.id)} className="text-red-500 hover:text-red-600" data-testid="delete-template-btn">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Link to Property */}
                  <div className="mt-4 pt-4 border-t">
                    <Label className="text-sm flex items-center gap-1 mb-2">
                      <Link2 className="h-3.5 w-3.5" /> Link to Property
                    </Label>
                    <Select
                      value={selectedTemplate.property_id || 'none'}
                      onValueChange={(v) => handleLinkProperty(selectedTemplate.id, v === 'none' ? null : v)}
                    >
                      <SelectTrigger className="w-72" data-testid="link-property-select">
                        <SelectValue placeholder="Select property..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not linked</SelectItem>
                        {properties.map(p => (<SelectItem key={p.id} value={p.id}>{p.name} - {p.developer}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <div className="grid lg:grid-cols-2 gap-4">
                {/* PDF Preview */}
                <Card className="border shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">PDF Preview</CardTitle>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => loadPagePreview(selectedTemplate.id, currentPage - 1)} disabled={currentPage === 0}>
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-slate-500">
                          {currentPage + 1} / {selectedTemplate.page_count}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => loadPagePreview(selectedTemplate.id, currentPage + 1)} disabled={currentPage >= selectedTemplate.page_count - 1}>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {previewLoading ? (
                      <div className="aspect-[1/1.414] bg-slate-100 rounded-lg flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                      </div>
                    ) : previewImage ? (
                      <img
                        src={`data:image/jpeg;base64,${previewImage}`}
                        alt="PDF Preview"
                        className="w-full border rounded-lg shadow-inner"
                        data-testid="pdf-preview-image"
                      />
                    ) : (
                      <div className="aspect-[1/1.414] bg-slate-100 rounded-lg flex items-center justify-center">
                        <FileText className="h-8 w-8 text-slate-300" />
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Field Mappings */}
                <Card className="border shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">
                        Field Mappings ({selectedTemplate.fields?.filter(f => f.enabled !== false).length || 0} active)
                      </CardTitle>
                      <Button size="sm" variant="outline" onClick={handleSaveFields} disabled={savingFields} data-testid="save-fields-btn">
                        {savingFields ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                        Save
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="max-h-[500px] overflow-y-auto">
                    {Object.entries(groupedFields).length > 0 ? (
                      Object.entries(groupedFields).map(([section, fields]) => (
                        <div key={section} className="mb-4">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{section}</h4>
                          <div className="space-y-1.5">
                            {fields.map((field) => (
                              <div
                                key={field.id}
                                className={`p-2 rounded border text-xs ${
                                  field.enabled === false ? 'bg-slate-50 opacity-50' : 'bg-white'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-1.5">
                                    <Switch
                                      checked={field.enabled !== false}
                                      onCheckedChange={() => toggleFieldEnabled(field.id)}
                                      className="scale-75"
                                    />
                                    <span className="font-medium text-slate-700 truncate max-w-[180px]">{field.label}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {field.required && <Badge variant="secondary" className="text-[10px] px-1 py-0 bg-red-50 text-red-600">Req</Badge>}
                                    <Badge variant="secondary" className="text-[10px] px-1 py-0">{field.field_type}</Badge>
                                  </div>
                                </div>
                                <div className="ml-7">
                                  <Input
                                    value={field.data_mapping || ''}
                                    onChange={(e) => updateField(field.id, 'data_mapping', e.target.value)}
                                    placeholder="form_data.field_name"
                                    className="h-6 text-[11px] font-mono"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : selectedTemplate.ai_detection_status === 'processing' ? (
                      <div className="text-center py-8 text-slate-500">
                        <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin opacity-50" />
                        <p className="text-sm">AI is detecting fields...</p>
                        <p className="text-xs mt-1">This may take 15-30 seconds</p>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No fields detected</p>
                        <Button size="sm" variant="outline" onClick={() => handleReDetect(selectedTemplate.id)} className="mt-2">
                          <Wand2 className="h-3 w-3 mr-1" />Run AI Detection
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Data Mapping Reference */}
              <Card className="border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Data Mapping Reference</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <h5 className="font-semibold text-slate-700 mb-1">Buyer Info</h5>
                      <ul className="space-y-0.5 text-slate-500 font-mono">
                        <li>form_data.buyer_name</li>
                        <li>form_data.buyer_ic</li>
                        <li>form_data.buyer_phone</li>
                        <li>form_data.buyer_email</li>
                        <li>form_data.buyer_address</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold text-slate-700 mb-1">Property</h5>
                      <ul className="space-y-0.5 text-slate-500 font-mono">
                        <li>property.name</li>
                        <li>unit.unit_number</li>
                        <li>unit.unit_type</li>
                        <li>unit.price</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold text-slate-700 mb-1">Agent</h5>
                      <ul className="space-y-0.5 text-slate-500 font-mono">
                        <li>agent.full_name</li>
                        <li>agent.agent_code</li>
                        <li>agent.phone</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold text-slate-700 mb-1">Transaction</h5>
                      <ul className="space-y-0.5 text-slate-500 font-mono">
                        <li>form_data.deposit_amount</li>
                        <li>form_data.bank_name</li>
                        <li>form_data.loan_amount</li>
                        <li>form_data.remarks</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="border shadow-sm h-full flex items-center justify-center min-h-[400px]">
              <div className="text-center text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Select a template</p>
                <p className="text-sm mt-1">or upload a new developer PDF</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDeveloperTemplates;
